"use client";

import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  className?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ className = "" }) => {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  // パンくずリストの生成
  const breadcrumbs: BreadcrumbItem[] = React.useMemo(() => {
    const items: BreadcrumbItem[] = [{ label: "Home", href: "/" }];
    
    pathnames.forEach((pathname, index) => {
      const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
      
      // パスに基づいてラベルを生成
      let label: string;
      switch (pathname) {
        case "showcase":
          label = "Showcase";
          break;
        case "blog":
          label = "Blog";
          break;
        case "playground":
          label = "Playground";
          break;
        case "ai-assistant":
          label = "AI Assistant";
          break;
        case "community":
          label = "Community";
          break;
        case "learn":
          label = "Learn";
          break;
        case "about":
          label = "About";
          break;
        case "observatory":
          label = "Observatory";
          break;
        case "hubble":
          label = "Hubble";
          break;
        case "jwst":
          label = "JWST";
          break;
        case "docs":
          label = "Documentation";
          break;
        default:
          // ID形式の場合はタイトルケースに変換
          if (pathname.match(/^[0-9]+-.+/)) {
            // スラッグからタイトルを生成 (例: "001-hello-demo" -> "Hello Demo")
            label = pathname
              .split("-")
              .slice(1)
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");
          } else {
            // それ以外はタイトルケースに変換
            label = pathname.charAt(0).toUpperCase() + pathname.slice(1);
          }
      }
      
      items.push({
        label,
        href: routeTo
      });
    });

    return items;
  }, [pathnames]);

  if (breadcrumbs.length <= 1) return null;

  return (
    <nav 
      className={`flex items-center text-sm text-gray-400 ${className}`}
      aria-label="パンくずリスト"
    >
      <ol className="flex items-center space-x-1">
        {breadcrumbs.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          
          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="w-4 h-4 mx-1 text-gray-600" />
              )}
              
              {isLast ? (
                <span className="text-white font-light text-contrast-high" aria-current="page">
                  {breadcrumb.label}
                </span>
              ) : (
                <Link
                  to={breadcrumb.href || "#"}
                  className="flex items-center text-gray-400 hover:text-white transition-colors font-light focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-black rounded text-contrast-medium"
                >
                  {index === 0 && <Home className="w-4 h-4 mr-1" />}
                  {breadcrumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;